/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import type { ListingColumn, ListingTableProps, ListingGroup } from './types';
import { compareByColumn, type SortDir } from './sort-comparator';

/**
 * Shared listing table for all data-list views across Housien.
 * Column-config-driven with optional sorting, row click, striping, and custom row props.
 */
export function ListingTable<T extends { id: string }>({
  data, columns, onRowClick, striped, getRowClassName, getRowProps, getRowStyle, testId, rowTestId, bare, minWidth,
  sortKey: controlledKey, sortDir: controlledDir, onSort,
  density = 'default',
  groups, renderGroupFooter,
}: ListingTableProps<T>) {
  const [internalKey, setInternalKey] = useState<string | null>(null);
  const [internalDir, setInternalDir] = useState<SortDir>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  // Controlled when the caller drives the sort (to order the full dataset
  // above pagination); otherwise the table owns the sort of the rows it holds.
  const isControlled = onSort !== undefined;
  const sortKey = isControlled ? controlledKey ?? null : internalKey;
  const sortDir = isControlled ? controlledDir ?? null : internalDir;

  const handleSort = (col: ListingColumn<T>) => {
    if (!col.sortable) return;
    if (isControlled) {
      onSort(col);
      return;
    }
    const isNewCol = internalKey !== col.key;
    const nextDir: SortDir = isNewCol ? 'asc' : internalDir === 'asc' ? 'desc' : null;
    setInternalKey(nextDir ? col.key : null);
    setInternalDir(nextDir);
  };

  const sortedData = useMemo(() => {
    // A controlled table has already been sorted by the caller across the
    // whole dataset — re-sorting the current page here would re-introduce the
    // page-scoped bug this mode exists to fix.
    if (isControlled) return data;
    if (!sortKey || !sortDir) return data;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return data;
    return [...data].sort((a, b) => compareByColumn(col, a, b, sortDir));
  }, [isControlled, data, columns, sortKey, sortDir]);

  /**
   * Renders one body row through the shared column `render`. Shared by the
   * flat and grouped layouts so a row looks identical in both.
   *
   * @param item - The row datum.
   * @param idx - The row index (for striping and `render`).
   * @returns The row element.
   */
  const renderRow = (item: T, idx: number) => {
    const extraProps = getRowProps?.(item) ?? {};
    const extraStyle = getRowStyle?.(item);
    const rowBg = hoveredRow === item.id
      ? 'var(--selection-active)'
      : striped && idx % 2 !== 0
        ? 'var(--surface-secondary)'
        : 'transparent';
    const cls = getRowClassName?.(item, idx) ?? '';
    return (
      <tr
        key={item.id}
        data-testid={rowTestId}
        onClick={() => onRowClick?.(item)}
        onMouseEnter={() => setHoveredRow(item.id)}
        onMouseLeave={() => setHoveredRow(null)}
        className={cls}
        style={{ borderBottom: '1px solid var(--border-ultra-subtle)', background: rowBg, cursor: onRowClick ? 'pointer' : 'default', transition: 'var(--transition-standard)', ...(density === 'compact' ? { height: COMPACT_ROW_HEIGHT } : {}), ...extraStyle }}
        {...extraProps}
      >
        {columns.map((col) => (
          <td key={col.key} style={bodyCellStyle(col, density)}>
            {col.render(item, idx)}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div style={bare ? bareContainerStyle : containerStyle} data-testid={testId}>
      <table style={minWidth ? { ...tableStyle, minWidth } : tableStyle}>
        <thead>
          <tr style={density === 'compact' ? compactHeaderRowStyle : headerRowStyle}>
            {columns.map((col) => (
              <th
                key={col.key}
                role="columnheader"
                aria-sort={
                  col.sortable && sortKey === col.key && sortDir
                    ? sortDir === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : col.sortable
                      ? 'none'
                      : undefined
                }
                onClick={() => handleSort(col)}
                style={headerCellStyle(col, density)}
              >
                <span style={headerContentStyle}>
                  {col.label}
                  {sortKey === col.key && sortDir && (
                    <span data-testid="sort-indicator" style={{ display: 'inline-flex', color: 'inherit' }}>
                      {sortDir === 'asc' ? <ChevronUpIcon width={12} height={12} /> : <ChevronDownIcon width={12} height={12} />}
                    </span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        {groups
          ? groups.map((group) => (
              <tbody key={group.key} data-testid="listing-group-body">
                <tr data-testid="listing-group-header" style={groupHeaderRowStyle} {...group.headerRowProps}>
                  {group.header}
                </tr>
                {!group.collapsed && group.rows.map((item, idx) => renderRow(item, idx))}
                {!group.collapsed && renderGroupFooter?.(group)}
              </tbody>
            ))
          : (
            <tbody>
              {sortedData.map((item, idx) => renderRow(item, idx))}
            </tbody>
          )}
      </table>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--border-subtle)',
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
  background: 'var(--surface-primary)',
  boxShadow: 'var(--shadow-sm)',
};

/** The same container without the card, for a table already inside one. */
const bareContainerStyle: React.CSSProperties = {
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
};

const tableStyle: React.CSSProperties = {
  width: '100%', minWidth: '720px', borderCollapse: 'collapse',
  fontFamily: 'var(--font-family)', tableLayout: 'fixed',
};

const headerRowStyle: React.CSSProperties = {
  background: 'var(--surface-tertiary)',
};

/** Fixed row height for `density="compact"`, in px. */
const COMPACT_ROW_HEIGHT = 64;

/** Group header row shell: the card surface + subtle top/bottom rules. */
const groupHeaderRowStyle: React.CSSProperties = {
  background: 'var(--surface-card)',
  borderTop: '1px solid var(--border-subtle)',
  borderBottom: '1px solid var(--border-subtle)',
  cursor: 'pointer',
};

const compactHeaderRowStyle: React.CSSProperties = {
  background: 'var(--surface-tertiary)',
  height: 36,
};

/**
 * Builds the inline style for a header cell, honoring the column alignment,
 * width, and sortable cursor affordance.
 *
 * @param col The column definition to style the header cell for.
 * @returns The CSS properties for the header cell.
 */
function headerCellStyle<T>(col: ListingColumn<T>, density: 'default' | 'compact' = 'default'): React.CSSProperties {
  return {
    padding: density === 'compact' ? '0 var(--space-4)' : 'var(--space-3) var(--space-4)',
    textAlign: col.align ?? 'left',
    fontSize: 'var(--text-meta)',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    cursor: col.sortable ? 'pointer' : 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    width: col.width,
  };
}

const headerContentStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', color: 'inherit',
};

/**
 * Builds the inline style for a body cell, honoring the column alignment
 * and width.
 *
 * @param col The column definition to style the body cell for.
 * @returns The CSS properties for the body cell.
 */
function bodyCellStyle<T>(col: ListingColumn<T>, density: 'default' | 'compact' = 'default'): React.CSSProperties {
  return {
    padding: density === 'compact' ? '0 var(--space-4)' : 'var(--space-3) var(--space-4)',
    fontSize: 'var(--text-body-sm)',
    color: 'var(--text-primary)',
    verticalAlign: 'middle',
    textAlign: col.align ?? 'left',
  };
}
